import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Table, Badge, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/layouts/Main';
import config from '../../config';
import axios from 'axios';
import ProductMovementModal from '../../components/modals/ProductMovementModal';

const InventoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inventoryData, setInventoryData] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [runningBatches, setRunningBatches] = useState([]);
  const [showMovementModal, setShowMovementModal] = useState(false);

  const fetchInventoryDetail = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch inventory data
      const inventoryResponse = await fetch(`${config.API_BASE_URL}/inventory/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!inventoryResponse.ok) throw new Error('Failed to fetch inventory data');

      const inventoryData = await inventoryResponse.json();
      const productInventory = inventoryData.products?.find(item => item.product._id === id);

      if (!productInventory) {
        throw new Error('Product not found in inventory');
      }

      setInventoryData(productInventory);
      setProductDetail(productInventory.product);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchPendingPOs = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/purchase-orders/pending-by-product/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setPendingPOs(response.data);
    } catch (err) {
      console.error('Error fetching pending POs:', err);
    }
  }, [id]);

  const fetchRunningBatches = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/production-batches/running-by-product/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setRunningBatches(response.data);
    } catch (err) {
      console.error('Error fetching running batches:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchInventoryDetail();
    if (id) {
      fetchPendingPOs();
      fetchRunningBatches();
    }
  }, [fetchInventoryDetail, fetchPendingPOs, fetchRunningBatches, id]);

  const formatStock = (value) => {
    if (value % 1 === 0) {
      return value.toString();
    }
    return value.toFixed(2);
  };

  const getStockStatus = (currentStock) => {
    if (currentStock === 0) {
      return { label: 'Out of Stock', variant: 'danger', color: 'text-danger' };
    } else if (currentStock <= 100) {
      return { label: 'Low Stock', variant: 'warning', color: 'text-warning' };
    } else {
      return { label: 'Normal Stock', variant: 'success', color: 'text-success' };
    }
  };

  if (loading) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading inventory details...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <div className="d-flex gap-2">
              <Button variant="outline-danger" onClick={fetchInventoryDetail}>
                Try Again
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/dashboard?tab=stock')}>
              {/* <Button variant="outline-secondary" onClick={() => navigate('/inventory')}> */}
                Back to Inventory
              </Button>
            </div>
          </Alert>
        </Container>
      </Layout>
    );
  }

  if (!inventoryData || !productDetail) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <Alert variant="warning">
            <Alert.Heading>Product Not Found</Alert.Heading>
            <p>The requested product could not be found in the inventory.</p>
            <Button variant="outline-secondary" onClick={() => navigate('/dashboard?tab=stock')}>
            {/* <Button variant="outline-secondary" onClick={() => navigate('/inventory')}> */}
              Back to Inventory
            </Button>
          </Alert>
        </Container>
      </Layout>
    );
  }

  const stockStatus = getStockStatus(inventoryData.totalAvailableStock);
  const unit = productDetail.unitId?.unitCode || '';

  return (
    <Layout>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/dashboard?tab=stock')}
                // onClick={() => navigate('/inventory')}
                className="d-flex align-items-center"
              >
                <i className="fas fa-arrow-left me-2"></i>Back
              </Button>
              <div>
                <h2 className="mb-0">{productDetail.productName}</h2>
                <p className="text-muted mb-0">Inventory Details & Stock Locations</p>
              </div>
            </div>
          </Col>
          <Col xs="auto">
            <div className="d-flex gap-2">
              {/* {inventoryData.totalAvailableStock <= 100 && (
                <Link
                  to={`/purchase-requests/new?product=${productDetail._id}`}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-2"></i>Create Purchase Request
                </Link>
              )} */}
              <Button
                variant="outline-info"
                onClick={() => setShowMovementModal(true)}
              >
                <i className="fas fa-history me-2"></i>View Movements
              </Button>
              <Button variant="outline-primary" onClick={fetchInventoryDetail}>
                <i className="fas fa-sync-alt me-2"></i>Refresh
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="g-4">
          {/* Product Information */}
          <Col lg={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Product Information</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Product Name:</strong>
                  <div>{productDetail.productName}</div>
                </div>
                
                {productDetail.alias && (
                  <div className="mb-3">
                    <strong>Alias:</strong>
                    <div>{productDetail.alias}</div>
                  </div>
                )}
                
                <div className="mb-3">
                  <strong>Product Code:</strong>
                  <div>{productDetail.productCode}</div>
                </div>
                
                <div className="mb-3">
                  <strong>SKU:</strong>
                  <div>{productDetail.sku || '-'}</div>
                </div>
                
                <div className="mb-3">
                  <strong>Category:</strong>
                  <div>
                    {productDetail.category ? (
                      <Badge bg="secondary">{productDetail.category}</Badge>
                    ) : '-'}
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>Unit:</strong>
                  <div>{productDetail.unitId?.unitName} ({unit})</div>
                </div>
                
                <div className="mb-3">
                  <strong>GST:</strong>
                  <div>{productDetail.gst}%</div>
                </div>
                
                {productDetail.cess !== undefined && (
                  <div className="mb-3">
                    <strong>Cess:</strong>
                    <div>{productDetail.cess}%</div>
                  </div>
                )}
                
                <div className="mb-3">
                  <strong>Status:</strong>
                  <div>
                    <Badge bg={productDetail.isActive ? 'success' : 'danger'}>
                      {productDetail.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Stock Summary */}
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Stock Summary</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h3 className={`mb-1 ${stockStatus.color}`}>
                        {formatStock(inventoryData.totalAvailableStock)} {unit}
                      </h3>
                      <small className="text-muted">Total Available Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h3 className="mb-1 text-primary">
                        {formatStock(inventoryData.unpackedStock?.reduce((sum, item) => sum + item.availableStock, 0) || 0)} {unit}
                      </h3>
                      <small className="text-muted">Unpacked Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h3 className="mb-1 text-success">
                        {formatStock(inventoryData.packedStock?.reduce((sum, item) => sum + item.availableStock, 0) || 0)} {unit}
                      </h3>
                      <small className="text-muted">Packed Stock</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center p-3 bg-light rounded">
                      <h3 className="mb-1 text-warning">
                        {formatStock(inventoryData.inPackingStock?.reduce((sum, item) => sum + item.availableStock, 0) || 0)} {unit}
                      </h3>
                      <small className="text-muted">Stock in Packing</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* In Packing Stock by Location */}
            {inventoryData.inPackingStock && inventoryData.inPackingStock.length > 0 && (
              <Card className="mb-4 border-warning">
                <Card.Header className="bg-warning text-dark">
                  <h5 className="mb-0">Stock Currently in Packing Process</h5>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Operation #</th>
                          <th>Storage Location</th>
                          <th>In-Packing Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryData.inPackingStock.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <Badge bg="dark">{item.operationNumber}</Badge>
                            </td>
                            <td>{item.warehouseLocation?.label || item.warehouseLocation?.name || 'Unknown'}</td>
                            <td>
                              <span className="fw-medium text-warning">
                                {formatStock(item.availableStock)} {unit}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Purchase Orders Pending */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Pending Purchase Orders</h5>
              </Card.Header>
              <Card.Body>
                {pendingPOs.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>PO Number</th>
                          <th>Vendor</th>
                          <th>Total Quantity</th>
                          <th>Received</th>
                          <th>Pending</th>
                          <th>Status</th>
                          <th>Expected Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingPOs.map((po) => (
                          <tr key={po._id}>
                            <td>
                              <Link to={`/purchase-orders/${po._id}`} className="text-decoration-none">
                                <strong>{po.purchaseOrderNumber}</strong>
                              </Link>
                              <br />
                              <small className="text-muted">
                                {new Date(po.createdAt).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div>
                                <strong>{po.vendor?.companyName || 'N/A'}</strong>
                                <br />
                                <small className="text-muted">{po.vendor?.contactPersonName || ''}</small>
                              </div>
                            </td>
                            <td>
                              <strong className="text-primary">
                                {po.totalQuantity} {productDetail?.unitId?.unitCode || ''}
                              </strong>
                            </td>
                            <td>
                              <strong className="text-success">
                                {po.receivedQuantity || 0} {productDetail?.unitId?.unitCode || ''}
                              </strong>
                            </td>
                            <td>
                              <strong className="text-warning">
                                {po.pendingQuantity} {productDetail?.unitId?.unitCode || ''}
                              </strong>
                            </td>
                            <td>
                              <Badge bg={po.status === 'Created' ? 'secondary' : 'warning'}>
                                {po.status}
                              </Badge>
                            </td>
                            <td>
                              {po.expectedDeliveryDate ? 
                                new Date(po.expectedDeliveryDate).toLocaleDateString() : 
                                'Not specified'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-shopping-cart fa-2x text-muted mb-2"></i>
                    <p className="text-muted mb-0">No pending purchase orders for this product</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Running Production Batches */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Running Production Batches (Input Material)</h5>
              </Card.Header>
              <Card.Body>
                {runningBatches.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Batch Number</th>
                          <th>Input Quantity</th>
                          <th>Status</th>
                          <th>Start Date</th>
                          <th>Completion</th>
                          <th>Production Request</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runningBatches.map((batch) => (
                          <tr key={batch._id}>
                            <td>
                              <Link to={`/batch/${batch._id}`} className="text-decoration-none">
                                <strong>{batch.batchNumber}</strong>
                              </Link>
                              <br />
                              <small className="text-muted">
                                {new Date(batch.createdAt).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <strong className="text-success">
                                {batch.quantity || 0} {productDetail?.unitId?.unitCode || ''}
                              </strong>
                            </td>
                            <td>
                              <Badge bg={
                                batch.status === 'draft' ? 'secondary' : 
                                batch.status === 'in-progress' ? 'warning' : 
                                batch.status === 'paused' ? 'danger' : 'info'
                              }>
                                {batch.status}
                              </Badge>
                            </td>
                            <td>
                              {batch.startedAt ? 
                                new Date(batch.startedAt).toLocaleDateString() : 
                                'Not started'
                              }
                            </td>
                            <td>
                              {batch.completedAt ? 
                                new Date(batch.completedAt).toLocaleDateString() : 
                                'In progress'
                              }
                            </td>
                            <td>
                              {batch.productionRequest?.requestNumber ? (
                                <Link
                                  to={`/production-requests/${batch.productionRequest._id}`}
                                  className="text-decoration-none"
                                >
                                  {batch.productionRequest.requestNumber}
                                </Link>
                              ) : (
                                <small className="text-muted">No PR linked</small>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-cogs fa-2x text-muted mb-2"></i>
                    <p className="text-muted mb-0">No running production batches using this material</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Unpacked Stock by Location */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Unpacked Stock by Location</h5>
              </Card.Header>
              <Card.Body>
                {inventoryData.unpackedStock && inventoryData.unpackedStock.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Storage Location</th>
                          <th>Available Stock</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryData.unpackedStock.map((location, index) => (
                          <tr key={location.warehouseLocation?._id || index}>
                            <td>
                              <div>
                                <div className="fw-medium">
                                  {location.warehouseLocation?.label || 'Unknown Location'}
                                </div>
                                {/* <small className="text-muted">
                                  ID: {location.warehouseLocation?._id || '-'}
                                </small> */}
                              </div>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {formatStock(location.availableStock)} {location.unit}
                              </span>
                            </td>
                            <td>
                              {location.lastUpdated
                                ? new Date(location.lastUpdated).toLocaleString('en-IN')
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Unpacked Stock</h5>
                    <p className="text-muted">This product has no unpacked stock in any warehouse locations.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Packed Stock by Location */}
            <Card>
              <Card.Header>
                <h5 className="mb-0">Packed Stock by Location</h5>
              </Card.Header>
              <Card.Body>
                {inventoryData.packedStock && inventoryData.packedStock.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Storage Location</th>
                          <th>Packing Details</th>
                          <th>Available Stock</th>
                          <th>Total Bags</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryData.packedStock.map((location, index) => (
                          <tr key={`${location.warehouseLocation?._id || index}-${location.packingMaterial?._id || 'no-pm'}`}>
                            <td>
                              <div>
                                <div className="fw-medium">
                                  {location.warehouseLocation?.label || 'Unknown Location'}
                                </div>
                                {/* <small className="text-muted">
                                  ID: {location.warehouseLocation?._id || '-'}
                                </small> */}
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="fw-medium">
                                  {location.packingMaterial?.name || 'Unknown Packing Material'}
                                </div>
                                <small className="text-muted">
                                  {location.quantityPerBag} {productDetail?.unitId?.unitCode || ''} per bag
                                </small>
                              </div>
                            </td>
                            <td>
                              <span className="fw-medium">
                                {formatStock(location.availableStock)} {location.unit}
                              </span>
                            </td>
                            <td>
                              <Badge bg="info" className="fs-10">
                                {location.totalBags} bags
                              </Badge>
                            </td>
                            <td>
                              {location.lastUpdated
                                ? new Date(location.lastUpdated).toLocaleString('en-IN')
                                : '-'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-box fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Packed Stock</h5>
                    <p className="text-muted">This product has no packed stock in any warehouse locations.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Product Movement Modal */}
        <ProductMovementModal
          show={showMovementModal}
          onHide={() => setShowMovementModal(false)}
          productId={id}
          productName={productDetail?.productName}
        />
      </Container>
    </Layout>
  );
};

export default InventoryDetail;
